import { t } from "./i18n";

export interface ImageSize {
  width: number;
  height: number;
}

export interface PetPhotoCompressionResult {
  file: File;
  originalSize: ImageSize;
  compressed: boolean;
}

const minJpegQuality = 0.72;
const qualityStep = 0.08;

export function calculateContainedSize(width: number, height: number, maxWidth: number, maxHeight: number): ImageSize {
  if (width <= 0 || height <= 0) {
    return { width: 0, height: 0 };
  }

  const scale = Math.min(1, maxWidth / width, maxHeight / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

export async function preparePetPhotoForUpload(
  file: File,
  maxFileSizeBytes: number,
  maxWidth: number,
  maxHeight: number
): Promise<PetPhotoCompressionResult> {
  const bitmap = await createBitmap(file);
  try {
    const originalSize = { width: bitmap.width, height: bitmap.height };
    const targetSize = calculateContainedSize(bitmap.width, bitmap.height, maxWidth, maxHeight);
    const needsResize = targetSize.width !== bitmap.width || targetSize.height !== bitmap.height;
    const needsCompress = file.size > maxFileSizeBytes || needsResize;

    if (!needsCompress) {
      return { file, originalSize, compressed: false };
    }

    const compressed = await compressBitmapToJpeg(bitmap, file.name, targetSize, maxFileSizeBytes);
    return { file: compressed, originalSize, compressed: true };
  } finally {
    bitmap.close?.();
  }
}

async function createBitmap(file: File): Promise<ImageBitmap> {
  if ("createImageBitmap" in globalThis) {
    return createImageBitmap(file, { imageOrientation: "from-image" });
  }

  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;
  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error(t("image.readUnsupported"));
  }

  context.drawImage(image, 0, 0);
  return createImageBitmap(canvas);
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(t("image.readFailed")));
    };
    image.src = url;
  });
}

async function compressBitmapToJpeg(
  bitmap: ImageBitmap,
  originalName: string,
  targetSize: ImageSize,
  maxFileSizeBytes: number
): Promise<File> {
  const canvas = document.createElement("canvas");
  canvas.width = targetSize.width;
  canvas.height = targetSize.height;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) {
    throw new Error(t("image.compressUnsupported"));
  }

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

  let latestBlob: Blob | null = null;
  for (let quality = 0.9; quality >= minJpegQuality; quality -= qualityStep) {
    latestBlob = await canvasToBlob(canvas, "image/jpeg", quality);
    if (latestBlob.size <= maxFileSizeBytes) {
      return new File([latestBlob], toCompressedJpegName(originalName), {
        type: "image/jpeg",
        lastModified: Date.now()
      });
    }
  }

  if (!latestBlob) {
    throw new Error(t("image.compressFailed"));
  }

  throw new Error(t("image.tooLargeAfterCompress"));
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error(t("image.compressFailed")));
      },
      type,
      quality
    );
  });
}

function toCompressedJpegName(fileName: string): string {
  const withoutExtension = fileName.replace(/\.[^.]+$/, "") || "pet-photo";
  return `${withoutExtension}-compressed.jpg`;
}
